import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Divider,
  TextField,
  Stack,
  Typography,
  Radio,
  CardContent,
} from '@mui/material';
import type { Account, RestoreAccount, SourceAccount } from '@repo/api-gw-sdk';
import { CloudAccountType, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';

import { useAccountRoleName } from './useAccountRoleName';
import { useSyncAccountPermissions } from './useSyncAccountPermissions';
import { useCloudFormationValidateAccount } from './validateAccount';

import { PanelWrapper } from '../panels/panelWrapper';
import { CopyableCode } from '../shared/copyableCode';

enum AWSDeploymentMethod {
  CloudFormation = 'CloudFormation',
  Terraform = 'Terraform',
}

export const RecreateAccountRoleAWSPanel = ({
  accountType,
  onUpdatedAccount,
  initialAccount,
  onClose,
}: {
  accountType: CloudAccountType;
  onUpdatedAccount: (sourceAccountId: string) => void;
  initialAccount: Account | SourceAccount | RestoreAccount;
  onClose: () => void;
}) => {
  const [awsAccountId, setAccountId] = useState(
    initialAccount.providerAccountId
  );
  const [hasError, setHasError] = useState(false);
  const [roleName, setRoleName] = useAccountRoleName(accountType);
  const [openConsoleClicked, setOpenConsoleClicked] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  const { openCloudFormationValidateAccount } =
    useCloudFormationValidateAccount();

  const validate = async () => {
    if (!user || !scanningAccountId) {
      return;
    }
    setOpenConsoleClicked(true);
    openCloudFormationValidateAccount(roleName, accountType);
  };

  const { syncAccountPermissions } = useSyncAccountPermissions(
    onUpdatedAccount,
    setUpdateInProgress,
    setHasError,
    initialAccount,
    false
  );

  const [deploymentMethod, setDeploymentMethod] = useState<AWSDeploymentMethod>(
    AWSDeploymentMethod.CloudFormation
  );

  const { user } = useUser();
  const dal = useDAL();

  const scanningAccountsList = dal.cloudAccounts.scanning.useList().data;
  const scanningAccountId = scanningAccountsList?.accounts?.find(
    (x) => x.cloudProvider === Provider.Aws
  )?.providerAccountId;

  const onUpdateClicked = () => {
    void syncAccountPermissions();
  };

  return (
    <PanelWrapper rounded header={{ title: 'Reinstall permissions', onClose }}>
      <CardContent>
        <Box className='px-[20px]'>
          <Box className='my-[15px] mb-[30px]'>
            <Stack direction='column' gap='16px'>
              <Typography marginBottom='8px' variant='body2'>
                Deployment method
              </Typography>
              <Stack
                className={'cursor-pointer'}
                direction='row'
                alignItems='center'
                gap='12px'
                onClick={() => {
                  setDeploymentMethod(AWSDeploymentMethod.CloudFormation);
                  setHasError(false);
                }}
              >
                <Radio
                  data-testid='full-instance-radio'
                  disabled={false}
                  size='small'
                  checked={
                    deploymentMethod === AWSDeploymentMethod.CloudFormation
                  }
                />
                <Typography variant='body2'>CloudFormation</Typography>
              </Stack>
              <Stack
                className={'cursor-pointer'}
                direction='row'
                alignItems='center'
                gap='12px'
                onClick={() => {
                  setDeploymentMethod(AWSDeploymentMethod.Terraform);
                  setHasError(false);
                }}
              >
                <Radio
                  disabled={false}
                  data-testid='specific-vol-radio'
                  size='small'
                  checked={deploymentMethod === AWSDeploymentMethod.Terraform}
                />
                <Typography variant='body2'>Terraform</Typography>
              </Stack>
            </Stack>
          </Box>
          <>
            <Box className='my-[15px]'>
              Before continuing, make sure you have permissions to create roles.
            </Box>
          </>
          <Box className='flex flex-col justify-center my-[30px] gap-[8px]'>
            <FormControl size='small' className='mb-[12px]'>
              <FormLabel>AWS account ID</FormLabel>
              <TextField
                size='medium'
                inputProps={{
                  sx: { height: '36px' },
                  maxLength: 12,
                  'data-testid': 'account-id',
                }}
                disabled={
                  deploymentMethod !== AWSDeploymentMethod.Terraform ||
                  !user ||
                  !scanningAccountId
                }
                value={awsAccountId}
                onChange={(event) => setAccountId(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && void validate()}
              />
            </FormControl>
            <FormControl size='small' className='mb-[12px]'>
              <FormLabel>Role name</FormLabel>
              <TextField
                size='medium'
                inputProps={{ style: { height: '36px' } }}
                value={roleName}
                disabled={
                  deploymentMethod !== AWSDeploymentMethod.Terraform &&
                  (updateInProgress || hasError)
                }
                onChange={(event) => setRoleName(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && void validate()}
              />
            </FormControl>
            {deploymentMethod === AWSDeploymentMethod.CloudFormation ? (
              <>
                {!openConsoleClicked && !updateInProgress && !hasError && (
                  <Button
                    variant='contained'
                    className='px-[20px] mt-[20px] w-auto'
                    sx={{ height: 48 }}
                    data-testid='open-aws-console'
                    disabled={!awsAccountId || !roleName}
                    onClick={() => void validate()}
                  >
                    Open AWS Console{' '}
                    <i className='material-symbols-open-in-new w-[20px] h-[20px] ml-[8px]' />
                  </Button>
                )}
                {(openConsoleClicked || updateInProgress) && (
                  <Box className='flex items-center gap-[8px] ml-[20px] justify-between'>
                    <Stack alignItems='center' direction='row' gap='12px'>
                      <Loader variant='inline' />
                      {updateInProgress ? (
                        <Typography variant='body2'>
                          Trying to connect...
                        </Typography>
                      ) : (
                        <Typography variant='body2'>
                          Waiting for permissions...
                        </Typography>
                      )}
                    </Stack>
                    {!updateInProgress && (
                      <>
                        <Button
                          variant='contained'
                          data-testid='aws-console-done'
                          onClick={onUpdateClicked}
                        >
                          Update
                        </Button>
                      </>
                    )}
                  </Box>
                )}
                {hasError && (
                  <Box
                    className='flex items-center gap-[4px] ml-[20px] h-[48px]'
                    sx={{ color: 'var(--mui-palette-error-main)' }}
                  >
                    <i className='material-symbols-close-rounded w-[20px] h-[20px]' />{' '}
                    Something went wrong
                    <Divider
                      orientation='vertical'
                      flexItem
                      className='mx-[6px] my-auto h-[20px]'
                    />
                    <Button
                      variant='contained'
                      onClick={() => void syncAccountPermissions()}
                    >
                      Try Again
                    </Button>
                  </Box>
                )}
              </>
            ) : null}
          </Box>
          <Box>
            {deploymentMethod === AWSDeploymentMethod.CloudFormation && (
              <Box className='my-[15px]'>
                We&apos;ll take you straight to the AWS console to set
                Eon&apos;s permissions. Everything is ready for you, just jump
                between the steps, confirm, and get back to us.
              </Box>
            )}
            {deploymentMethod === AWSDeploymentMethod.Terraform && (
              <>
                <Typography
                  variant='body2'
                  className='mt-[28px] mb-[8px] font-bold'
                  color='text.secondary'
                >
                  Account deployment
                </Typography>

                <ol className='list-decimal mx-[20px]'>
                  <li className='mb-[8px]'>
                    <Typography
                      variant='body2'
                      className='mb-[8px]'
                      color='text.secondary'
                    >
                      <b>
                        Add the Eon Terraform module to your deployment file
                      </b>
                      <br />
                      Add this snippet to your Terraform deployment file and
                      apply.
                    </Typography>
                  </li>
                </ol>
                <Box
                  className='flex flex-col items-center justify-center my-[30px]'
                  gap='8px'
                >
                  <CopyableCode
                    code={
                      accountType === CloudAccountType.Source
                        ? `module "eon_source_account_infra" {
    source = "https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/eon-aws-source-account-tf.zip"
    eon_account_id = "${user?.eonAccount.id}"
    scanning_account_id = "${scanningAccountId}"
    role_name = "${roleName}"
  }`
                        : `module "eon_restore_account_infra" {
    source = "https://eon-public-b2b628cc-1d96-4fda-8dae-c3b1ad3ea03b.s3.amazonaws.com/eon-aws-restore-account-tf.zip"
    eon_account_id = "${user?.eonAccount.id}" 
    role_name = "${roleName}"
  }`
                    } // SurDin: why can user be undefined?
                  />
                </Box>
                <ol start={2} className='list-decimal mx-[20px]'>
                  <li>
                    <Typography
                      variant='body2'
                      className='mb-[8px]'
                      color='text.secondary'
                    >
                      <b>Confirm the deployment</b>
                      <br />
                      After the Terraform deployment is done, click the button
                      below to validate and connect the account to your Eon
                      account.
                    </Typography>
                  </li>
                </ol>

                <Box
                  className='flex flex-col items-center justify-center mb-[8px]'
                  gap='8px'
                >
                  <Button
                    variant='contained'
                    disabled={false}
                    data-testid='azure-connect-complete-btn'
                    onClick={onUpdateClicked}
                    sx={{ width: '400px', mt: '24px' }}
                  >
                    Connect account
                  </Button>
                </Box>
                {hasError && (
                  <Typography
                    variant='body1'
                    color='error'
                    data-testid='error-message'
                  >
                    <Stack
                      className='my-[15px]'
                      direction='row'
                      alignItems='center'
                    >
                      <i className='material-symbols-error-outline-rounded mr-[8px] text-[24px]'></i>
                      Error encountered
                    </Stack>
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </PanelWrapper>
  );
};
